(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;
  const PENDING_REVIEWS_STORAGE_KEY = 'jft-testimonials-pending-reviews';
  // A human needs longer than this to read the form and type a review.
  const MIN_SUBMIT_DELAY_MS = 20000;

  function getYotpo() {
    return root.JFTYotpo || {};
  }

  function reviewIdentityKey(review) {
    return `${review?.name || ''}|${review?.title || ''}|${review?.content || ''}`.toLowerCase();
  }

  function buildWindowedPageNumbers(currentPage, totalPages, siblingCount = 1) {
    const total = Math.max(1, Number(totalPages) || 1);
    const current = Math.min(total, Math.max(1, Number(currentPage) || 1));
    const siblings = Math.max(0, Number(siblingCount) || 0);

    if (total <= 7) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const pages = new Set([1, total]);
    for (let page = current - siblings; page <= current + siblings; page += 1) {
      if (page >= 1 && page <= total) {
        pages.add(page);
      }
    }

    const sorted = [...pages].sort((left, right) => left - right);
    const items = [];
    let previous = 0;

    sorted.forEach((page) => {
      if (previous && page - previous > 1) {
        items.push('...');
      }

      items.push(page);
      previous = page;
    });

    return items;
  }

  function readPendingReviewsFromStorage() {
    try {
      const raw = root.sessionStorage?.getItem(PENDING_REVIEWS_STORAGE_KEY);
      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writePendingReviewsToStorage(reviews) {
    try {
      if (!root.sessionStorage) {
        return;
      }

      if (!reviews.length) {
        root.sessionStorage.removeItem(PENDING_REVIEWS_STORAGE_KEY);
        return;
      }

      root.sessionStorage.setItem(PENDING_REVIEWS_STORAGE_KEY, JSON.stringify(reviews));
    } catch (error) {
      // Ignore storage quota / private mode failures.
    }
  }

  function registerTestimonialsReviews() {
    if (!root.document || !root.Alpine) {
      return;
    }

    const yotpo = getYotpo();
    if (!yotpo.fetchReviewsPage || !yotpo.normalizeTestimonialReview) {
      return;
    }

    root.Alpine.data('testimonialsPageReviews', (config = {}) => ({
      appKey: config.appKey || '',
      tooFastLabel: config.tooFastLabel || 'Thanks! Please take a moment to look over your review, then post it again.',
      activeFilter: 'all',
      mediaReviews: [],
      mediaLoading: false,
      mediaLoaded: false,
      reviewOnProductTemplate: config.reviewOnProductTemplate || 'on __PRODUCT__',
      reviewMediaAltTemplate: config.reviewMediaAltTemplate || "Photo from __NAME__'s review",
      botField: '',
      formRenderedAt: 0,
      sourceId: config.sourceId || '',
      locale: config.locale || 'en-US',
      useYotpo: config.useYotpo !== false,
      shopDomain: config.shopDomain || '',
      shopName: config.shopName || 'JustFishyThing',
      productUrl: config.productUrl || '',
      fallbackRating: config.fallbackRating || '4.9',
      reviewCountTemplate: config.reviewCountTemplate || '__COUNT__ ratings',
      reviewCountOneTemplate: config.reviewCountOneTemplate || '',
      yotpoSecret: config.yotpoSecret || '',
      fallbackDistribution: config.fallbackDistribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      ratingLabelTemplate: config.ratingLabelTemplate || '{{ rating }} out of 5 stars',
      loadingLabel: config.loadingLabel || 'Loading reviews',
      postReviewLabel: config.postReviewLabel || 'Post review',
      thanksFeedbackLabel: config.thanksFeedbackLabel || 'Thanks for your feedback',
      pageSize: Number(config.pageSize) || 9,
      maxPages: Number(config.maxPages) || 10,
      currentPage: 1,
      formOpen: true,
      selectedRating: 4,
      reviewTitle: '',
      reviewBody: '',
      reviewName: '',
      reviewEmail: '',
      submitting: false,
      submitSuccess: false,
      submitError: '',
      successResetTimer: null,
      loading: true,
      pageLoading: false,
      loadError: false,
      reviews: [],
      pageCache: {},
      totalReviews: 0,
      totalCount: 0,
      averageScore: 0,
      distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      displayReviewCountLabel: '',
      pendingOptimisticReviews: [],
      adminRecentReviews: [],
      adminFeedReady: false,
      adminAuthFailed: false,
      countSource: 'fallback',

      init() {
        this.loadSourceData();
        this.pendingOptimisticReviews = readPendingReviewsFromStorage();
        this.syncReviewCountLabel();

        if (!this.useYotpo || !this.appKey) {
          this.loading = false;
          return;
        }

        this.bootstrapReviews();
      },

      async bootstrapReviews() {
        const secret = String(this.yotpoSecret || '').trim();
        console.log('[Yotpo Testimonials] bootstrapReviews', {
          hasSecretKey: Boolean(secret),
          mode: secret ? 'widget-list + admin-total/overlay' : 'widget-only',
        });

        // Widget API is the storefront feed (correct newest-first pagination).
        // Admin API is only used for live totals + newest overlays — its default
        // page order is oldest-first and incomplete for browsing.
        if (secret) {
          await this.resolveAdminReviewData();
          console.log('[Yotpo Testimonials] admin auth result', {
            adminAuthFailed: this.adminAuthFailed,
            adminOverlayCount: this.adminRecentReviews.length,
            totalReviews: this.totalReviews,
            countSource: this.countSource,
          });
        }

        await this.fetchPage(this.currentPage);
      },

      loadSourceData() {
        if (!this.sourceId || !root.document) {
          return;
        }

        const source = root.document.getElementById(this.sourceId);
        if (!source) {
          return;
        }

        try {
          const data = JSON.parse(source.textContent || '{}');
          this.appKey = data.appKey || this.appKey;
          this.locale = data.locale || this.locale;
          this.useYotpo = data.useYotpo !== false;
          this.shopDomain = data.shopDomain || this.shopDomain;
          this.shopName = data.shopName || this.shopName;
          this.productUrl = data.productUrl || this.productUrl;
          this.fallbackRating = data.fallbackRating || this.fallbackRating;
          this.reviewCountTemplate = data.reviewCountTemplate || this.reviewCountTemplate;
          this.reviewCountOneTemplate = data.reviewCountOneTemplate || this.reviewCountOneTemplate;
          this.yotpoSecret = data.yotpoSecret || this.yotpoSecret;
          this.fallbackDistribution = data.fallbackDistribution || this.fallbackDistribution;
          this.ratingLabelTemplate = data.ratingLabelTemplate || this.ratingLabelTemplate;
          this.loadingLabel = data.loadingLabel || this.loadingLabel;
          this.postReviewLabel = data.postReviewLabel || this.postReviewLabel;
          this.thanksFeedbackLabel = data.thanksFeedbackLabel || this.thanksFeedbackLabel;
          this.pageSize = Number(data.pageSize) || this.pageSize;
          this.maxPages = Number(data.maxPages) || this.maxPages;
          this.syncReviewCountLabel();

          const secret = String(this.yotpoSecret || '').trim();
          console.log('[Yotpo Testimonials] config loaded', {
            sourceId: this.sourceId,
            hasAppKey: Boolean(this.appKey),
            appKeyPreview: this.appKey ? `${String(this.appKey).slice(0, 6)}...` : '',
            hasSecretKey: Boolean(secret),
            secretKeyLength: secret.length,
            secretKeyPreview: secret ? `${secret.slice(0, 4)}...${secret.slice(-4)}` : '(empty)',
          });
        } catch (error) {
          console.warn('[Yotpo Testimonials] failed to parse config', error);
          // Keep defaults when config JSON is unavailable.
        }
      },

      formatReviewCountLabel(count) {
        const safeCount = Math.max(0, Number(count) || 0);
        const template = String(
          (safeCount === 1 && this.reviewCountOneTemplate) || this.reviewCountTemplate || '__COUNT__ ratings',
        );

        if (!safeCount) {
          return '';
        }

        if (template.includes('__COUNT__')) {
          return template.replace('__COUNT__', String(safeCount));
        }

        if (template.includes('{{ count }}')) {
          return template.replace('{{ count }}', String(safeCount));
        }

        return `${safeCount} ratings`;
      },

      syncReviewCountLabel() {
        const liveTotal = Number(this.totalReviews) || 0;
        this.displayReviewCountLabel = liveTotal > 0
          ? this.formatReviewCountLabel(liveTotal)
          : '';
      },

      setLiveReviewTotal(total, source = 'live') {
        const nextTotal = Math.max(0, Number(total) || 0);

        if (!nextTotal) {
          return;
        }

        if (source === 'admin') {
          this.countSource = 'admin';
        } else if (this.countSource !== 'admin') {
          this.countSource = source;
        }

        // Never decrease after optimistic bumps or admin totals.
        this.totalReviews = Math.max(Number(this.totalReviews) || 0, nextTotal);

        // Pagination stays on the public widget feed size, not the Admin/marketing total.
        if (source === 'live') {
          this.totalCount = Math.max(this.totalCount || 0, nextTotal);
        }

        this.syncReviewCountLabel();
      },

      persistPendingReviews() {
        writePendingReviewsToStorage(this.pendingOptimisticReviews);
      },

      async resolveAdminReviewData() {
        const secret = String(this.yotpoSecret || '').trim();

        if (!secret) {
          return false;
        }

        try {
          // Total: paginate Admin reviews (count=100) and sum until last short page.
          if (typeof yotpo.fetchAccountReviewTotal === 'function') {
            const total = await yotpo.fetchAccountReviewTotal(this.appKey, secret);
            if (total > 0) {
              this.setLiveReviewTotal(total, 'admin');
            }
          }

          const fetchNewest = typeof yotpo.fetchNewestAccountReviews === 'function'
            ? yotpo.fetchNewestAccountReviews.bind(yotpo)
            : null;
          const fetchAdminPage = yotpo.fetchAccountReviewsPage
            || ((key, sec, opts) => yotpo.fetchRecentAccountReviews(key, sec, opts));

          const payload = fetchNewest
            ? await fetchNewest(this.appKey, secret, { count: 50 })
            : await fetchAdminPage(this.appKey, secret, {
              page: 1,
              count: 50,
              forceNewestFirst: true,
            });

          const { reviews, total } = payload;

          if (total > 0) {
            this.setLiveReviewTotal(total, 'admin');
          }

          this.adminRecentReviews = (reviews || []).map((review) => (
            yotpo.normalizeTestimonialReview(review, this.locale)
          ));
          // Never drive the main grid from admin pages (oldest-first / partial).
          this.adminFeedReady = false;
          this.adminAuthFailed = false;
          this.syncReviewCountLabel();

          console.log('[Yotpo Testimonials] admin total + newest overlay', {
            total: this.totalReviews,
            overlayCount: this.adminRecentReviews.length,
            newestDate: this.adminRecentReviews[0]?.date || null,
            countSource: this.countSource,
          });

          return this.adminRecentReviews.length > 0 || this.totalReviews > 0;
        } catch (error) {
          console.warn('[Yotpo Testimonials] Admin API failed', error);
          this.adminFeedReady = false;
          this.adminAuthFailed = true;
          this.adminRecentReviews = [];
          this.syncReviewCountLabel();
          return false;
        }
      },

      mergePageOneReviews(productReviews, siteReviews) {
        const productIds = new Set(
          productReviews.map((review) => String(review?.id || '')).filter(Boolean)
        );
        const siteOnly = (siteReviews || []).filter((review) => {
          const id = String(review?.id || '');
          return !id || !productIds.has(id);
        });

        return [...siteOnly, ...productReviews]
          .sort((left, right) => {
            const leftTime = new Date(left?.created_at || left?.createdAt || 0).getTime();
            const rightTime = new Date(right?.created_at || right?.createdAt || 0).getTime();
            return rightTime - leftTime;
          });
      },

      async fetchAdminPage(page) {
        const secret = String(this.yotpoSecret || '').trim();
        const fetchAdminPage = yotpo.fetchAccountReviewsPage
          || ((key, sec, opts) => yotpo.fetchRecentAccountReviews(key, sec, opts));

        const { reviews, total } = await fetchAdminPage(this.appKey, secret, {
          page,
          count: this.pageSize,
        });

        const normalized = (reviews || [])
          .map((review) => yotpo.normalizeTestimonialReview(review, this.locale))
          .sort((left, right) => {
            const leftTime = new Date(left?.createdAt || 0).getTime();
            const rightTime = new Date(right?.createdAt || 0).getTime();
            return rightTime - leftTime;
          });

        if (total > 0) {
          this.totalCount = Math.max(this.totalCount || 0, total);
          this.setLiveReviewTotal(total, 'admin');
        }

        if (normalized.length) {
          const pageAverage = Number(yotpo.computeAverageScore(normalized)) || 0;
          if (page === 1 && pageAverage) {
            this.averageScore = pageAverage;
          } else if (!this.averageScore && pageAverage) {
            this.averageScore = pageAverage;
          }
        }

        console.log('[Yotpo Testimonials] admin page loaded', {
          page,
          count: normalized.length,
          newestDate: normalized[0]?.date || null,
          oldestOnPage: normalized[normalized.length - 1]?.date || null,
        });

        if (typeof yotpo.resolveDistributionState === 'function' && page === 1) {
          const resolved = yotpo.resolveDistributionState({
            averageScore: this.averageScore,
            reviews: normalized,
            fallbackPercents: this.fallbackDistribution,
          });
          this.distribution = resolved.distribution;
          this.fallbackDistribution = resolved.fallbackPercents;
        }

        this.pageCache[page] = normalized;
        this.reviews = this.applyOverlayReviews(normalized, page);
        this.loadError = normalized.length === 0 && page === 1;
      },

      async fetchPage(page) {
        if (!this.appKey) {
          this.loading = false;
          this.loadError = true;
          return;
        }

        const targetPage = Math.max(1, Number(page) || 1);
        const isInitial = this.reviews.length === 0;

        if (this.pageCache[targetPage]) {
          this.reviews = this.applyOverlayReviews(this.pageCache[targetPage], targetPage);
          return;
        }

        if (isInitial) {
          this.loading = true;
        } else {
          this.pageLoading = true;
        }

        try {
          const [{ reviews, bottomline, pagination, products }, sitePage] = await Promise.all([
            yotpo.fetchReviewsPage(this.appKey, {
              page: targetPage,
              perPage: this.pageSize,
            }),
            targetPage === 1 && typeof yotpo.fetchSiteReviewsPage === 'function'
              ? yotpo.fetchSiteReviewsPage(this.appKey, {
                page: 1,
                perPage: Math.max(this.pageSize, 20),
              }).catch(() => null)
              : Promise.resolve(null),
          ]);

          let mergedRaw = reviews;

          if (targetPage === 1) {
            mergedRaw = this.mergePageOneReviews(reviews, sitePage?.reviews || []);
          }

          const productNames = yotpo.buildProductNameMap([
            ...(products || []),
            ...(sitePage?.products || []),
          ]);

          let normalized = mergedRaw
            .map((review) => yotpo.normalizeTestimonialReview(review, this.locale, productNames))
            .sort((left, right) => {
              const leftTime = new Date(left?.createdAt || 0).getTime();
              const rightTime = new Date(right?.createdAt || 0).getTime();
              return rightTime - leftTime;
            })
            .slice(0, this.pageSize);

          this.pageCache[targetPage] = normalized;
          this.reviews = this.applyOverlayReviews(normalized, targetPage);

          const paginationTotal = Number(pagination.total) || 0;
          const bottomlineTotal = Number(bottomline.total_review ?? bottomline.totalReview) || 0;
          const resolvedTotal = paginationTotal || bottomlineTotal;

          // The widget feed is authoritative for browsable storefront pages.
          if (resolvedTotal) {
            this.totalCount = resolvedTotal;
          } else if (targetPage === 1) {
            this.totalCount = normalized.length;
          }

          // Live summary count prefers admin total when available.
          if (this.countSource !== 'admin') {
            const liveTotal = bottomlineTotal || resolvedTotal || normalized.length;
            if (liveTotal) {
              this.setLiveReviewTotal(liveTotal, 'live');
            }
          }

          const averageScore = Number(
            bottomline.average_score
            ?? bottomline.averageScore
            ?? yotpo.computeAverageScore(normalized)
          ) || 0;
          if (averageScore) {
            this.averageScore = averageScore;
          }

          if (typeof yotpo.resolveDistributionState === 'function') {
            const resolved = yotpo.resolveDistributionState({
              starDistribution: bottomline.star_distribution || bottomline.starDistribution,
              averageScore,
              reviews: normalized,
              fallbackPercents: this.fallbackDistribution,
            });
            this.distribution = resolved.distribution;
            this.fallbackDistribution = resolved.fallbackPercents;
          } else {
            const parsedDistribution = yotpo.parseStarDistribution(
              bottomline.star_distribution || bottomline.starDistribution
            );

            if (parsedDistribution) {
              this.distribution = parsedDistribution;
            } else {
              this.distribution = yotpo.buildDistribution(normalized);
            }
          }

          this.loadError = false;
          console.log('[Yotpo Testimonials] widget page loaded', {
            page: targetPage,
            totalCount: this.totalCount,
            totalPages: Math.max(1, Math.ceil((this.totalCount || 0) / this.pageSize)),
            newestDate: this.reviews[0]?.date || null,
            countSource: this.countSource,
          });
        } catch (error) {
          if (isInitial) {
            this.loadError = true;
            this.reviews = [];
          }
        } finally {
          this.loading = false;
          this.pageLoading = false;
        }
      },

      applyOverlayReviews(reviews, page) {
        if (page !== 1) {
          return reviews;
        }

        const existingKeys = new Set(reviews.map((review) => reviewIdentityKey(review)));
        const overlays = [];

        this.adminRecentReviews.forEach((review) => {
          const key = reviewIdentityKey(review);
          if (!existingKeys.has(key)) {
            overlays.push(review);
            existingKeys.add(key);
          }
        });

        const stillPending = [];
        this.pendingOptimisticReviews.forEach((review) => {
          const key = reviewIdentityKey(review);
          if (existingKeys.has(key)) {
            return;
          }

          overlays.push(review);
          existingKeys.add(key);
          stillPending.push(review);
        });

        this.pendingOptimisticReviews = stillPending;
        this.persistPendingReviews();

        if (!overlays.length) {
          return reviews;
        }

        return [...overlays, ...reviews]
          .sort((left, right) => {
            const leftTime = new Date(left?.createdAt || 0).getTime();
            const rightTime = new Date(right?.createdAt || 0).getTime();
            return rightTime - leftTime;
          })
          .slice(0, this.pageSize);
      },

      buildOptimisticReview({ score, title, content, name }) {
        const displayName = String(name || '').trim() || 'Customer';

        return {
          id: `local-${Date.now()}`,
          score: Number(score) || 0,
          title: String(title || '').trim(),
          content: String(content || '').trim(),
          name: displayName,
          initial: displayName.charAt(0).toUpperCase() || 'C',
          date: new Intl.DateTimeFormat(this.locale || 'en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          }).format(new Date()),
        };
      },

      get yotpoReady() {
        return this.useYotpo && !this.loadError && this.reviews.length > 0;
      },

      get averageScoreDisplay() {
        if (this.yotpoReady) {
          return Number(this.averageScore).toFixed(1);
        }

        return this.fallbackRating;
      },

      get reviewCountLabel() {
        const liveTotal = Number(this.totalReviews) || 0;
        if (liveTotal > 0) {
          return this.formatReviewCountLabel(liveTotal);
        }

        return this.displayReviewCountLabel || '';
      },

      bumpDisplayedReviewCount() {
        const nextTotal = Math.max(0, Number(this.totalReviews) || 0) + 1;
        this.totalReviews = nextTotal;
        this.syncReviewCountLabel();
      },

      get summaryRatingDisplay() {
        return this.averageScoreDisplay;
      },

      get summaryReviewsLabel() {
        return this.reviewCountLabel;
      },

      get summaryRatingLabel() {
        return this.ratingLabelTemplate
          .replace('__RATING__', String(this.summaryRatingDisplay))
          .replace('{{ rating }}', String(this.summaryRatingDisplay));
      },

      summaryStarFill(star) {
        const rating = Number(this.averageScoreDisplay) || 0;
        const fill = rating - (Number(star) - 1);

        if (fill <= 0) {
          return 0;
        }

        if (fill >= 1) {
          return 100;
        }

        return Math.round(fill * 100);
      },

      get distributionRows() {
        return yotpo.buildDistributionRows(this.distribution, this.fallbackDistribution);
      },

      get totalPages() {
        // Filtered views are a complete client-side list, not a server page.
        if (this.activeFilter !== 'all') {
          return 1;
        }

        if (!this.totalCount) {
          return 1;
        }

        return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
      },

      get filteredReviews() {
        if (this.activeFilter === 'media') {
          return this.mediaReviews;
        }

        return Array.isArray(this.reviews) ? this.reviews : [];
      },

      // Page one mixes site reviews, which never carry photos, with product
      // reviews, which do -- so the loaded page says nothing about whether
      // photos exist anywhere. Offer the filter until the fetch proves the set
      // is empty, rather than hiding a working feature behind an unrelated
      // sample. The count stays hidden until the real number is known.
      get hasAnyMedia() {
        return this.mediaLoaded ? this.mediaReviews.length > 0 : true;
      },

      get mediaReviewCount() {
        return this.mediaLoaded ? this.mediaReviews.length : null;
      },

      // Reviews arrive a page at a time, so filtering the loaded page would only
      // ever surface the handful of photos on it. This pulls the full set once,
      // in large pages, and keeps the ones with photos.
      async loadMediaReviews() {
        if (this.mediaLoaded || this.mediaLoading || !this.appKey) {
          return;
        }

        this.mediaLoading = true;

        try {
          const CHUNK = 100;
          const MAX_PAGES = 5;
          const wanted = Number(this.totalCount) || CHUNK;
          const pages = Math.min(MAX_PAGES, Math.max(1, Math.ceil(wanted / CHUNK)));
          const collected = [];
          const seen = new Set();

          for (let page = 1; page <= pages; page += 1) {
            // eslint-disable-next-line no-await-in-loop
            const { reviews, products } = await yotpo.fetchReviewsPage(this.appKey, {
              page,
              perPage: CHUNK,
            });

            const productNames = yotpo.buildProductNameMap(products);

            if (!Array.isArray(reviews) || reviews.length === 0) {
              break;
            }

            reviews.forEach((raw) => {
              const review = yotpo.normalizeTestimonialReview(raw, this.locale, productNames);
              if (!review.hasMedia || seen.has(review.id)) {
                return;
              }
              seen.add(review.id);
              collected.push(review);
            });

            if (reviews.length < CHUNK) {
              break;
            }
          }

          collected.sort((left, right) => (
            new Date(right?.createdAt || 0).getTime() - new Date(left?.createdAt || 0).getTime()
          ));

          this.mediaReviews = collected;
          this.mediaLoaded = true;

          // Nothing to show: drop back so the visitor is never left staring at
          // an empty list they cannot explain.
          if (collected.length === 0 && this.activeFilter === 'media') {
            this.activeFilter = 'all';
          }
        } catch (error) {
          // Leave mediaLoaded false so a later click retries rather than
          // stranding the filter on an empty list.
          this.mediaReviews = [];
        } finally {
          this.mediaLoading = false;
        }
      },

      get paginatedReviews() {
        return this.filteredReviews;
      },

      reviewOnProduct(review) {
        // Store reviews have no product, and the template would otherwise render
        // a dangling "on ". x-show hides the element too; this makes the text
        // itself safe regardless of how it is bound.
        const name = String(review?.productName || '').trim();

        if (!name) {
          return '';
        }

        return String(this.reviewOnProductTemplate).replace('__PRODUCT__', name);
      },

      reviewMediaAlt(review) {
        return String(this.reviewMediaAltTemplate).replace('__NAME__', review?.name || 'a customer');
      },

      setReviewFilter(filter) {
        if (this.activeFilter === filter) {
          return;
        }

        this.activeFilter = filter;
        // The server paginates the unfiltered set, so jumping to a later page
        // while filtered would ask Yotpo for a page that no longer lines up.
        this.currentPage = 1;

        if (filter === 'media') {
          this.loadMediaReviews();
        }
      },

      get pageNumbers() {
        return buildWindowedPageNumbers(this.currentPage, this.totalPages, 1);
      },

      get canGoPrev() {
        return this.currentPage > 1;
      },

      get canGoNext() {
        return this.currentPage < this.totalPages;
      },

      get canSubmitReview() {
        if (this.submitting) {
          return false;
        }

        const email = String(this.reviewEmail || '').trim();
        const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

        if (this.selectedRating < 1) {
          return false;
        }

        if (!String(this.reviewTitle || '').trim()) {
          return false;
        }

        if (!String(this.reviewBody || '').trim()) {
          return false;
        }

        if (!String(this.reviewName || '').trim()) {
          return false;
        }

        return emailValid;
      },

      get isSubmitDisabled() {
        return this.submitting || this.submitSuccess || !this.canSubmitReview;
      },

      get submitButtonLabel() {
        if (this.submitSuccess) {
          return this.thanksFeedbackLabel;
        }

        return this.postReviewLabel;
      },

      reviewRatingLabel(score) {
        return this.ratingLabelTemplate
          .replace('__RATING__', String(score))
          .replace('{{ rating }}', String(score));
      },

      selectReviewRating(score, moveFocus = false) {
        const normalizedScore = Math.min(5, Math.max(1, Number(score) || 1));
        this.selectedRating = normalizedScore;

        if (moveFocus) {
          this.$nextTick(() => {
            this.$root
              ?.querySelector(`[data-review-rating="${normalizedScore}"]`)
              ?.focus({ preventScroll: true });
          });
        }
      },

      moveReviewRating(score, direction) {
        const currentScore = Math.min(5, Math.max(1, Number(score) || 1));
        const step = Number(direction) < 0 ? -1 : 1;
        let nextScore = currentScore + step;

        if (nextScore > 5) {
          nextScore = 1;
        } else if (nextScore < 1) {
          nextScore = 5;
        }

        this.selectReviewRating(nextScore, true);
      },

      resetReviewForm() {
        this.selectedRating = 4;
        this.reviewTitle = '';
        this.reviewBody = '';
        this.reviewName = '';
        this.reviewEmail = '';
        this.submitError = '';
      },

      clearSuccessState() {
        if (this.successResetTimer) {
          root.clearTimeout(this.successResetTimer);
          this.successResetTimer = null;
        }

        this.submitSuccess = false;
      },

      async refreshReviewsAfterSubmit() {
        this.pageCache = {};
        this.currentPage = 1;
        await this.fetchPage(1);
      },

      async submitReview() {
        if (this.isSubmitDisabled || typeof yotpo.createReview !== 'function') {
          return;
        }

        // Cheap bot filters. Neither is a real defence -- the Yotpo endpoint and
        // the app key are both public, so anything determined can post directly
        // without loading this page -- but they cost nothing and stop the
        // scripted form fills. Fail silently: a specific error message just
        // tells the author which check to defeat next time.
        if (String(this.botField || '').trim() !== '') {
          return;
        }

        // Unlike the honeypot, this one tells the visitor what happened. A bot can
        // discover the threshold just by waiting, so hiding it buys nothing --
        // whereas silently dropping a genuine review that was typed quickly
        // loses the review and the customer never knows.
        const shownFor = Date.now() - Number(this.formRenderedAt || 0);
        if (this.formRenderedAt && shownFor < MIN_SUBMIT_DELAY_MS) {
          this.submitError = this.tooFastLabel;
          return;
        }

        this.submitting = true;
        this.submitError = '';
        this.clearSuccessState();

        const optimistic = this.buildOptimisticReview({
          score: this.selectedRating,
          title: String(this.reviewTitle || '').trim(),
          content: String(this.reviewBody || '').trim(),
          name: String(this.reviewName || '').trim(),
        });

        try {
          await yotpo.createReview(this.appKey, {
            domain: this.shopDomain || root.location.hostname,
            sku: 'yotpo_site_reviews',
            productTitle: this.shopName,
            productUrl: this.productUrl || root.location.origin,
            displayName: optimistic.name,
            email: String(this.reviewEmail || '').trim(),
            reviewTitle: optimistic.title,
            reviewContent: optimistic.content,
            reviewScore: optimistic.score,
          });

          this.resetReviewForm();
          this.submitSuccess = true;
          this.pendingOptimisticReviews = [optimistic, ...this.pendingOptimisticReviews];
          this.persistPendingReviews();
          this.bumpDisplayedReviewCount();
          await this.refreshReviewsAfterSubmit();

          this.successResetTimer = root.setTimeout(() => {
            this.submitSuccess = false;
            this.successResetTimer = null;
          }, 3000);
        } catch (error) {
          const rawMessage = String(error?.message || '');
          console.warn('[Yotpo Testimonials] create review failed', {
            message: rawMessage,
            title: optimistic.title,
            email: String(this.reviewEmail || '').trim(),
          });

          if (/duplicate/i.test(rawMessage)) {
            this.submitError = 'Yotpo rejected this as a duplicate review (matched against existing store reviews, IP, or a prior submit). Try a unique title and longer unique wording, or wait and use another network/device.';
          } else {
            this.submitError = rawMessage || 'Unable to post review right now. Please try again.';
          }
        } finally {
          this.submitting = false;
        }
      },

      goToPage(page) {
        const nextPage = Number(page);
        if (!nextPage || nextPage < 1 || nextPage > this.totalPages || nextPage === this.currentPage) {
          return;
        }

        this.currentPage = nextPage;
        this.fetchPage(nextPage);
      },

      prevPage() {
        if (!this.canGoPrev) {
          return;
        }

        this.currentPage -= 1;
        this.fetchPage(this.currentPage);
      },

      nextPage() {
        if (!this.canGoNext) {
          return;
        }

        this.currentPage += 1;
        this.fetchPage(this.currentPage);
      },
    }));
  }

  if (root.document) {
    root.document.addEventListener('alpine:init', registerTestimonialsReviews);

    if (root.Alpine) {
      registerTestimonialsReviews();
    }
  }
})();
